import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import { changeActiveTab, selectTabs, TabType } from "@/reducers/tabsSlice"
import { cn } from "@/lib/utils"
import { TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip"
import { Tooltip } from "@/components/ui/tooltip"
import { motion } from "framer-motion"
import type * as React from "react"
import type { LucideIcon, LucideProps } from "lucide-react"
import { useEventCallback } from "@/hooks/use-event-callback"
import { useSidebarNavigation } from "./SidebarContext"

// Type to handle both LucideIcon and our custom icons
type IconComponent =
  | LucideIcon
  | React.ComponentType<LucideProps>
  | React.ForwardRefExoticComponent<
      LucideProps & React.RefAttributes<SVGSVGElement>
    >

interface BaseSidebarButtonProps {
  tabName: TabType
  icon: IconComponent
  activeIcon: IconComponent
  tooltipText: string
  className?: string
  onClick?: () => void
  children?: React.ReactNode
}

const BaseSidebarButton = ({
  tabName,
  icon,
  activeIcon,
  tooltipText,
  className,
  onClick,
  children,
}: BaseSidebarButtonProps) => {
  const { activeTab } = useAppSelector(selectTabs)
  const active = activeTab === tabName
  const dispatch = useAppDispatch()
  const onNavigate = useSidebarNavigation()

  // Select the appropriate icon based on active state
  const IconComponent = active ? activeIcon : icon

  const handleClick = useEventCallback(() => {
    onNavigate?.()
    if (active) return
    dispatch(
      changeActiveTab({
        activeTab: tabName,
      }),
    )
    onClick?.()
  })

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleClick}
            // The icon is aria-hidden and there is no text child, so without a
            // label this button has no accessible name: a screen reader
            // announces "button", and getByRole(name) cannot find it either.
            aria-label={tooltipText}
            data-testid={`sidebar-tab-${tabName}`}
            className={cn(
              "bg:transparent relative flex h-8 w-8 items-center justify-center rounded-[6px] p-1.5 hover:bg-sidebar",
              className,
            )}
          >
            {active && (
              <motion.span
                layoutId="bubble"
                className="absolute inset-0 z-10 bg-[#e5e5e5] dark:bg-[#474747]"
                style={{ borderRadius: 6 }}
                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
              />
            )}
            <IconComponent className="z-20 h-5 w-5" />
            {children}
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default BaseSidebarButton
